import React from 'react'
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  FormHelperText,
  FormControl,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import ListService from 'api/services/Lists'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
// import { ReactComponent as PencilSVG } from 'assets/svg/new-icons/pencil.svg'
import { ListProperty } from 'types'
import { Check, Close, EditOutlined } from '@mui/icons-material'
import { useSnackbar } from 'notistack'

interface FormFields {
  name: string
  oldName: string
}

interface Props {
  list: ListProperty
  onUpdate: (arg: ListProperty) => void
}
function ListName(props: Props) {
  const { list, onUpdate } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  React.useEffect(() => {
    reset({
      name: list.name ?? '',
      oldName: list.name ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.name])

  const updateMutation = ListService.hooks.useUpdateList({
    onError: () => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
    onSuccess: (data) => {
      reset({ name: data.name ?? '', oldName: data.name ?? '' })
      setEditable(false)
      enqueueSnackbar(t('ListPage.messages.success'), {
        variant: 'success',
      })
      onUpdate(data)
    },
  })

  const [editable, setEditable] = React.useState(false)

  const handleMakeEditable = () => {
    setEditable(true)
    reset({
      name: list.name ?? '',
      oldName: list.name ?? '',
    })
  }

  const handleClose = () => {
    setEditable(false)
    reset({ name: list.name ?? '', oldName: list.name ?? '' })
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
  }

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: {
      name: '',
      oldName: '',
    },
  })

  const watchedName = watch('name', '')
  const onSubmit: SubmitHandler<FormFields> = (data) => {
    if (data.name.trim() !== data.oldName.trim()) {
      updateMutation.mutate({
        listId: list.id,
        name: data.name.trim(),
      })
    } else {
      setEditable(false)
    }
  }

  return (
    <>
      <Controller
        name="name"
        control={control}
        rules={{
          required: t(`ListPage.validation.name`) as unknown as string,
          maxLength: {
            value: 40,
            message: t('ListPage.messages.inputMaxLength'),
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <FormControl>
              <TextField
                placeholder={t('listDetailView.actions.updateListName')}
                variant="standard"
                {...field}
                sx={{
                  width: editable
                    ? '400px'
                    : watchedName && watchedName?.length < 50
                    ? `${watchedName?.length + 9}ch`
                    : '400px',
                  '& .MuiInput-root': {
                    fontWeight: 900,
                    fontSize: '20px',
                  },
                }}
                helperText={
                  error ? (
                    <Typography
                      color="textPrimary"
                      sx={{ fontWeight: 500, fontSize: '14px' }}
                    >
                      {error?.message}
                    </Typography>
                  ) : null
                }
                // helperText={
                //   <Typography
                //     color="textPrimary"
                //     sx={{ fontWeight: 500, fontSize: '14px' }}
                //   >
                //     {t(`enums.listCategory.${list?.listCategory}`)}
                //     {list.userFavorites?.length
                //       ? ' (' +
                //         t(`generics.count`, {
                //           count: list.userFavorites?.length,
                //         }) +
                //         ')'
                //       : null}
                //   </Typography>
                // }
                InputProps={{
                  readOnly: !editable,
                  disableUnderline: !editable,
                  endAdornment: (
                    <InputAdornment position="end">
                      {editable ? (
                        <IconButton
                          onClick={() => handleSubmit(onSubmit)()}
                          onMouseDown={handleMouseDown}
                          edge="end"
                        >
                          <Check sx={{ color: 'success.main' }} />
                        </IconButton>
                      ) : null}
                      <IconButton
                        onClick={handleMakeEditable}
                        onMouseDown={handleMouseDown}
                        edge="end"
                      >
                        {editable ? null : <EditOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormHelperText sx={{ ml: 0, mt: 0 }}>
                <Typography
                  color="textPrimary"
                  sx={{ fontWeight: 500, fontSize: '14px' }}
                >
                  {t(`enums.listCategory.${list?.listCategory}`)}
                  {list.userFavorites?.length
                    ? ' (' +
                      t(`generics.count`, {
                        count: list.userFavorites?.length,
                      }) +
                      ')'
                    : null}
                </Typography>
              </FormHelperText>
            </FormControl>
            {editable ? (
              <IconButton
                size="small"
                onClick={handleClose}
                onMouseDown={handleMouseDown}
                sx={{ mb: '25px', ml: '8px' }}
              >
                <Close color="action" />
              </IconButton>
            ) : null}
          </Box>
        )}
      />
    </>
  )
}

export default ListName
